import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Search, Phone, Mail, X, Users, AlertCircle } from 'lucide-react';
import { Customer, getCustomers } from '../../lib/customers';
import { useBusiness } from '../../contexts/BusinessContext';
import CustomerModal from '../CustomerModal';
import { CustomerRepeatTrackingService, CustomerType } from '../../lib/customerRepeatTracking';

interface SelectCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (customer: Customer) => void;
}

// Component to display customer repeat status badge
function CustomerRepeatBadge({ customerId }: { customerId: string }) {
  const [customerType, setCustomerType] = useState<CustomerType | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadCustomerType = async () => {
      if (!customerId) return;
      
      setLoading(true);
      try {
        const type = await CustomerRepeatTrackingService.getCustomerType(customerId);
        setCustomerType(type);
      } catch (error) {
        console.error('Error loading customer type:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCustomerType();
  }, [customerId]);

  if (loading) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
        Loading...
      </span>
    );
  }

  if (!customerType) return null;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
      customerType === 'repeat' 
        ? 'bg-green-100 text-green-800 border border-green-200' 
        : 'bg-blue-100 text-blue-800 border border-blue-200'
    }`}>
      {customerType === 'repeat' ? 'Repeat' : 'New'} Customer
    </span>
  );
}

export default function SelectCustomerModal({
  isOpen,
  onClose,
  onSelect
}: SelectCustomerModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [displayedCustomers, setDisplayedCustomers] = useState<Customer[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const customersPerPage = 15;


  const loadCustomers = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const data = await getCustomers(selectedBusiness.id);
      
      // Debug logging for customer loading issues
      console.log('📊 [CUSTOMER LOAD DEBUG] Total customers loaded:', data.length);
      const candiceCustomers = data.filter(c => 
        c.first_name?.toLowerCase().includes('candice') || 
        c.email?.toLowerCase().includes('candice.pelke@gmail.com')
      );
      console.log('📊 [CUSTOMER LOAD DEBUG] Candice customers found:', candiceCustomers.length, candiceCustomers);
      
      setCustomers(data);
      setError(null);
    } catch (err) {
      console.error('Error loading customers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadCustomers();
    }
  }, [isOpen, selectedBusiness?.id]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
      setError(null);
      setCurrentPage(1);
      setHasMore(true);
    }
  };

  const handleCustomerCreated = async (newCustomer: Customer) => {
    setIsCustomerModalOpen(false);
    await loadCustomers();
    
    // Automatically select the newly created customer
    onSelect(newCustomer);
  };



  // Filter customers based on search query
  const filteredCustomers = searchQuery ? customers.filter(customer => {
    const searchLower = searchQuery.toLowerCase();
    const nameMatch = `${customer.first_name} ${customer.last_name}`.toLowerCase().includes(searchLower);
    const emailMatch = customer.email?.toLowerCase().includes(searchLower);
    const phoneMatch = customer.phone?.includes(searchLower);
    
    // Debug logging for search issues
    if (searchLower.includes('candice') || searchLower.includes('pelke')) {
      console.log('🔍 [SEARCH DEBUG] Checking customer:', {
        name: `${customer.first_name} ${customer.last_name}`,
        email: customer.email,
        phone: customer.phone,
        status: customer.status,
        business_id: customer.business_id,
        searchQuery: searchLower,
        nameMatch,
        emailMatch,
        phoneMatch,
        finalMatch: nameMatch || emailMatch || phoneMatch
      });
    }
    
    return nameMatch || emailMatch || phoneMatch;
  }) : customers;

  // Handle pagination and search display
  useEffect(() => {
    if (searchQuery) {
      // When searching, show all filtered results (no pagination)
      setDisplayedCustomers(filteredCustomers);
      setCurrentPage(1);
      setHasMore(false);
    } else {
      // When not searching, apply pagination
      const startIndex = 0;
      const endIndex = currentPage * customersPerPage;
      const paginatedCustomers = filteredCustomers.slice(startIndex, endIndex);
      
      setDisplayedCustomers(paginatedCustomers);
      setHasMore(endIndex < filteredCustomers.length);
    }
  }, [filteredCustomers, searchQuery, currentPage]);

  // Reset pagination when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleLoadMore = () => {
    if (!searchQuery) {
      setCurrentPage(prev => prev + 1);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100020]`}
        onClick={onClose}
        style={{
          zIndex: 100020,
          transform: 'translateZ(0)',
          WebkitTransform: 'translateZ(0)',
          isolation: 'isolate',
          touchAction: 'manipulation',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        }}
      >
        <div 
          className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          onTransitionEnd={handleTransitionEnd}
          onClick={e => e.stopPropagation()}
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="bg-black border-b border-gray-800">
              <div className="flex items-center justify-between px-6 py-4">
                <h2 className="text-xl font-semibold text-white">
                  Select Customer
                </h2>
                <button 
                  onClick={onClose} 
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>


            
            {/* Search and Add Customer Section */}
            <div className="bg-white border-b border-gray-200 p-6">
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search customers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                  />
                </div>
                <button
                  onClick={() => setIsCustomerModalOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap"
                >
                  <Plus className="h-4 w-4 mr-2 inline" />
                  New Customer
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto bg-gray-50">
              {error && (
                <div className="m-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                    <p className="mt-4 text-sm text-gray-600">Loading customers...</p>
                  </div>
                </div>
              ) : displayedCustomers.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
                  <p className="text-sm text-gray-600">
                    {searchQuery ? 'Try adjusting your search terms' : 'Get started by adding your first customer'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mx-6 mb-6">
                    {displayedCustomers.map((customer) => (
                      <div
                        key={customer.id}
                        className="p-4 hover:bg-gray-50 transition-colors duration-150 cursor-pointer border-b border-gray-100 last:border-b-0"
                        onClick={() => onSelect(customer)}
                      >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-base font-medium text-blue-600">
                              {customer.first_name && customer.first_name[0]}
                              {customer.last_name && customer.last_name[0]}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="font-medium text-gray-900 truncate">
                                {customer.first_name} {customer.last_name}
                              </div>
                              <CustomerRepeatBadge customerId={customer.id} />
                            </div>
                            <div className="flex items-center text-sm text-gray-600 truncate mb-1">
                              <Mail className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                              {customer.email}
                            </div>
                            {customer.phone && (
                              <div className="flex items-center text-sm text-gray-500">
                                <Phone className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                                {customer.phone}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            customer.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}
                          </span>
                        </div>
                      </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Load More Button */}
                  {hasMore && !searchQuery && (
                    <div className="px-6 pb-6">
                      <button
                        onClick={handleLoadMore}
                        className="w-full px-4 py-3 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
                      >
                        Load More Customers ({displayedCustomers.length} of {filteredCustomers.length})
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      
      <CustomerModal
        isOpen={isCustomerModalOpen}
        onClose={() => setIsCustomerModalOpen(false)}
        onSuccess={handleCustomerCreated}
        businessId={selectedBusiness?.id || ''}
      />
    </>
  );
}